import React, { useLayoutEffect, useRef, useState } from "react";
import { Link, generatePath, useLocation } from "react-router";
import { IconButton, Menu, Tabs, Tooltip, VerticalDivider } from "@sscs/ui";
import { ArrowDropDownIcon, CloseIcon, DescriptionIcon } from "@sscs/ui/icons";
import { FULL_REPORT_PATH } from "Constants";
import { useReports } from "Hooks";
import { ReportFileSummary } from "Types";
import { generateReportSearchParams } from "Utils";
import { computeVisibleCount } from "./computeVisibleCount";
import styles from "./reportTabs.module.scss";

const getReportLinkTo = (
  summary: ReportFileSummary,
): { pathname: string; search: string } => ({
  pathname: generatePath(`${FULL_REPORT_PATH}/*`, {
    groupId: summary.groupId,
    projectName: summary.projectName,
    packageName: summary.packageName,
    version: summary.version,
    "*": summary.goToPage ?? "",
  }),
  search: generateReportSearchParams({
    isRepro: summary.isRepro,
    diffWith: summary.diffWith,
    existingParams: summary.searchParams,
  }),
});

const ReportTabs = (): React.JSX.Element | null => {
  const { reports, closeReport, openedReportKey, isReportPath } = useReports();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [visibleCount, setVisibleCount] = useState(Number.POSITIVE_INFINITY);

  const entries = Object.entries(reports);
  const total = entries.length;
  const measureSignature = entries
    .map(([key, summary]) => `${key}:${summary.tabLabel}`)
    .join("|");

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const recompute = () => {
      const pills = Array.from(
        measure.querySelectorAll<HTMLElement>("[data-measure-pill]"),
      );
      const pillWidths = pills.map(
        (pill) => pill.getBoundingClientRect().width,
      );
      const chip = measure.querySelector<HTMLElement>("[data-measure-chip]");
      const chipWidth = chip ? chip.getBoundingClientRect().width : 0;
      const gap =
        pills.length >= 2
          ? pills[1].getBoundingClientRect().left -
            pills[0].getBoundingClientRect().right
          : 0;

      setVisibleCount(
        computeVisibleCount(pillWidths, chipWidth, container.clientWidth, gap),
      );
    };

    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    observer.observe(measure);
    recompute();

    return () => observer.disconnect();
  }, [measureSignature]);

  if (total === 0) return null;

  const count = Math.max(1, Math.min(visibleCount, total));
  let visibleEntries = entries.slice(0, count);
  let hiddenEntries = entries.slice(count);

  // The active report is always visible: if it fell into the hidden set, drop it
  // into the last visible slot and push the displaced pill into the menu. This is
  // render-time only — the store order never changes — and it keeps the MUI Tabs
  // `value` pointing at a rendered tab.
  const activeIsHidden =
    isReportPath &&
    openedReportKey !== undefined &&
    hiddenEntries.some(([key]) => key === openedReportKey);

  if (activeIsHidden) {
    const displaced = visibleEntries[visibleEntries.length - 1];
    const promoted = hiddenEntries.find(([key]) => key === openedReportKey)!;
    visibleEntries = [...visibleEntries.slice(0, -1), promoted];
    hiddenEntries = [
      displaced,
      ...hiddenEntries.filter(([key]) => key !== openedReportKey),
    ];
  }

  const hiddenCount = hiddenEntries.length;
  const activeIsVisible =
    isReportPath &&
    openedReportKey !== undefined &&
    visibleEntries.some(([key]) => key === openedReportKey);

  const renderTab = (
    reportKey: string,
    summary: ReportFileSummary,
    index: number,
    forMeasure: boolean,
  ): React.JSX.Element => (
    <Tabs.Tab
      key={reportKey}
      value={reportKey}
      label={
        <Tooltip title={summary.tabLabel}>
          <div className={styles["report-title"]}>
            <DescriptionIcon />
            <span className={styles["report-label"]}>{summary.tabLabel}</span>
          </div>
        </Tooltip>
      }
      classes={{ root: styles["tab-root"] }}
      id={`report-tab-${index}`}
      icon={
        <IconButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closeReport(reportKey);
          }}
          size="small"
          className={styles["close-button"]}
        >
          <CloseIcon />
        </IconButton>
      }
      iconPosition="end"
      component={Link}
      to={openedReportKey === reportKey ? location : getReportLinkTo(summary)}
      {...(forMeasure ? { "data-measure-pill": true } : {})}
    />
  );

  return (
    <>
      <VerticalDivider />
      <div className={styles.container} ref={containerRef}>
        <Tabs.Tabs
          value={activeIsVisible ? openedReportKey : false}
          aria-label="Spectra Assure reports"
          classes={{ list: styles["flex-container"] }}
        >
          {visibleEntries.map(([reportKey, summary], index) =>
            renderTab(reportKey, summary, index, false),
          )}
        </Tabs.Tabs>

        {hiddenCount > 0 && (
          <button
            type="button"
            className={styles["overflow-chip"]}
            aria-label={`${hiddenCount} more reports`}
            aria-haspopup="menu"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            +{hiddenCount}
            <ArrowDropDownIcon />
          </button>
        )}

        <div className={styles["measure-strip"]} aria-hidden ref={measureRef}>
          <Tabs.Tabs value={false} classes={{ list: styles["flex-container"] }}>
            {entries.map(([reportKey, summary], index) =>
              renderTab(reportKey, summary, index, true),
            )}
          </Tabs.Tabs>
          <button
            type="button"
            className={styles["overflow-chip"]}
            data-measure-chip
            tabIndex={-1}
          >
            +{total - 1}
            <ArrowDropDownIcon />
          </button>
        </div>

        <Menu.Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {hiddenEntries.map(([reportKey, summary]) => (
            <Menu.Item
              key={reportKey}
              component={Link}
              to={getReportLinkTo(summary)}
              onClick={() => setAnchorEl(null)}
            >
              <DescriptionIcon />
              <span className={styles["menu-item-label"]}>
                {summary.tabLabel}
              </span>
            </Menu.Item>
          ))}
        </Menu.Menu>
      </div>
    </>
  );
};

export default ReportTabs;
